import { Badge } from '@/components/ui/badge';

interface ContentBlock {
  type: string;
  [key: string]: any;
}

interface OnboardingBlockRendererProps {
  blocks: ContentBlock[];
}

export const OnboardingBlockRenderer = ({ blocks }: OnboardingBlockRendererProps) => {
  return (
    <div className="space-y-4">
      {blocks.map((block, i) => (
        <RenderBlock key={i} block={block} />
      ))}
    </div>
  );
};

const RenderBlock = ({ block }: { block: ContentBlock }) => {
  switch (block.type) {
    case 'text':
      return (
        <p
          className={`text-muted-foreground ${
            block.align === 'center' ? 'text-center' : ''
          } ${block.size === 'lg' ? 'text-lg' : block.size === 'xs' ? 'text-xs' : block.size === 'sm' ? 'text-sm' : ''} ${
            block.italic ? 'italic' : ''
          }`}
          dangerouslySetInnerHTML={{ __html: block.content }}
        />
      );

    case 'grid':
      return (
        <div className={`grid grid-cols-1 ${
          block.columns === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'
        } gap-3 mt-2`}>
          {block.items?.map((item: any, i: number) => (
            <div key={i} className="p-4 rounded-xl border bg-card">
              {item.emoji && <span className="text-xl">{item.emoji}</span>}
              <p className="font-semibold">{item.title}</p>
              <p className="text-xs text-muted-foreground">{item.description}</p>
            </div>
          ))}
        </div>
      );

    case 'card_list':
      return (
        <div className="space-y-3">
          {block.items?.map((item: any, i: number) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-lg border bg-card">
              {item.emoji && <span className="text-2xl">{item.emoji}</span>}
              <div>
                <p className="font-medium">{item.title}</p>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      );

    case 'tab_list':
      return (
        <div className="space-y-2">
          {block.items?.map((item: any, i: number) => (
            <div key={i} className="flex items-start gap-3 p-2 rounded-lg border bg-card">
              <span className="text-xs font-mono bg-muted px-2 py-0.5 rounded mt-0.5 shrink-0">
                {item.label}
              </span>
              <span className="text-sm text-muted-foreground">{item.description}</span>
            </div>
          ))}
        </div>
      );

    case 'bullet_card':
      return (
        <div className="p-4 rounded-xl border bg-card">
          <p className="font-medium">{block.title}</p>
          <ul className="text-sm text-muted-foreground mt-2 space-y-1 list-disc list-inside">
            {block.items?.map((item: string, i: number) => (
              <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
            ))}
          </ul>
        </div>
      );

    case 'highlight_list':
      return (
        <div className="p-4 rounded-xl border-2 border-primary/30 bg-primary/5">
          <p className="font-medium text-center">{block.title}</p>
          <ul className="text-sm text-muted-foreground mt-2 space-y-1 list-disc list-inside">
            {block.items?.map((item: string, i: number) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
      );

    case 'numbered_list':
      return (
        <div className="space-y-2 text-left">
          {block.items?.map((item: string, i: number) => (
            <div key={i} className="flex items-start gap-3 p-3 rounded-lg border bg-card">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0">
                {i + 1}
              </span>
              <span className="text-sm">{item}</span>
            </div>
          ))}
        </div>
      );

    case 'highlight':
      return (
        <div className="p-6 rounded-xl border-2 border-dashed border-primary/30 bg-primary/5 text-center">
          <p className="text-xl font-bold text-primary">{block.title}</p>
          {block.description && (
            <p className="text-sm text-muted-foreground mt-2">{block.description}</p>
          )}
        </div>
      );

    default:
      return null;
  }
};
